/**
 * rental-calendar.js — Self-hosted booking calendar for TechRental
 * Generic: reads plans/prices from data-variants (Shopify product.variants)
 * so no per-model hardcoding is needed.
 *
 * Design 2.0 (NOB-1047): mobile-first, fixed-size widget, 2x2 plans,
 * inline extension pills, always-6-week calendar, delivery/return row,
 * tap-to-reveal time slot overlay, sticky bottom bar.
 *
 * Dependencies: None (vanilla JS)
 * Loaded by: sections/rental-calendar.liquid
 */

(function () {
  'use strict';

  // ── Configuration ──

  // availability API を DB と同じ東京リージョンで実行させる（NOB-1766）。
  // Supabase Edge Functions は呼び出し元の最寄りリージョンで動くため、海外・
  // AI エージェント経由では関数が東京の DB と何往復もして 1.5〜3 秒かかっていた。
  // クエリパラメータなので CORS のプリフライトは増えない。
  // cart-deadline-guard.js の REGION_QUERY・templates/agents.md.liquid の案内と同じ値を保つこと。
  var REGION_QUERY = '&forceFunctionRegion=ap-northeast-1';

  // Variant title → rental days mapping.
  var TITLE_TO_DAYS = [
    { keyword: '1泊2日', days: 2 },
    { keyword: '3泊4日', days: 4 },
    { keyword: '2週間', days: 14 },
    { keyword: '1週間', days: 7 }
  ];

  function resolveDays(title) {
    for (var i = 0; i < TITLE_TO_DAYS.length; i++) {
      if (title.indexOf(TITLE_TO_DAYS[i].keyword) !== -1) {
        return TITLE_TO_DAYS[i].days;
      }
    }
    return null;
  }

  var PLANS = [];
  var EXTENSIONS = [{ label: '延長なし', days: 0, price: 0, variantId: null }];
  // 満室時の代替機種候補（NOB-1123）。sections/rental-calendar.liquid の
  // data-alt-products から読む: [{slug, label, url, prices: {days: price}}]。
  // 自機種は init で除外済み。
  var ALT_PRODUCTS = [];

  var TIME_SLOTS = [
    { value: '08-12', label: '8:00~12:00' },
    { value: '14-16', label: '14:00~16:00' },
    { value: '16-18', label: '16:00~18:00' },
    { value: '18-20', label: '18:00~20:00' },
    { value: '19-21', label: '19:00~21:00' },
    { value: 'none',  label: '指定なし' }
  ];

  var SHIP_LEAD_DAYS = 2;
  var RETURN_BUFFER_DAYS = 3;

  // availability API v2 (NOB-1290): 日付ごとに {remaining, status} が返る。
  // remaining はこの値でキャップ（4=4台以上、正確な保有規模は非開示）。
  // ペイロードに無い日付は REMAINING_CAP 台以上空いている、が契約。
  var REMAINING_CAP = 4;
  // v1レスポンスをフォールバック解釈する時のみ使用（v2では「残りわずか」の
  // しきい値はサーバ側判定 status==='low' に一元化されている）。
  var LOW_STOCK_THRESHOLD = 3;

  var LEAD_TIME_DAYS_BASE = 2;
  var LATE_CUTOFF_HOUR_JST = 15;

  var WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

  // ── 早割（NOB-1616 / 親 NOB-1614）──
  // 設定はショップ metafield techrental.early_bird を sections/rental-calendar.liquid が
  // data-early-bird={minLeadDays, percent} で渡す（ログイン中の会員なら Liquid 側が
  // member_min_lead_days を minLeadDays に入れてくる）。無ければ null＝早割の表示は一切しない
  // （ops が設定スクリプトの --disable で metafield を消せばテーマも消える）。
  // 実額は TechRental-ops の割引関数 early-bird-discount が決済時点で引く。ここは表示だけで、
  // 14・10 などの数字を持たない。判定の規則（開始日の JST 日付 − 今日 >= minLeadDays、行ごとに
  // % を掛けて丸める）は関数側 src/early-bird.ts と同じにしてある。
  var EARLY_BIRD = null;

  // ── State ──
  var state = {
    selectedPlanIndex: 1,
    selectedExtensionIndex: 0,
    selectedStartDate: null,
    selectedTimeSlot: null,
    timeslotOpen: false,
    currentMonth: null,
    availability: null, // {dateStr: {remaining, low}} — v2ペイロード由来
    shipLeadDays: SHIP_LEAD_DAYS,
    returnBufferDays: RETURN_BUFFER_DAYS,
    minDate: null, // API min_date — earliest bookable day
    maxDate: null, // API max_date — latest bookable day (caps the 6-week grid spillover)
    loading: true,
    error: null,
    validating: false, // add-to-cart server re-check in flight (blocks re-entry)
    addedToCart: false, // GA4 funnel: did this session reach add-to-cart success?
    // NOB-1123: グレー日タップへの応答。{dateStr, reason, altShownFired} | null
    rejection: null,
    // NOB-1123: 代替機種の空き。{slug: {availability, shipLeadDays, returnBufferDays, minDate, maxDate}} | null
    altData: null,
    // NOB-1123: 別機種チップからの着地パラメータ（?tr_start&tr_days）。{start, days} | null
    prefill: null,
    // NOB-1123後続: 日付選択直後に時間帯ボタンを1回パルスさせるフラグ
    slotNudge: false,
    // NOB-1616: 直前の描画で早割の割引後価格を見せたか（カート追加前の再確認に使う）
    earlyBirdShown: false
  };

  var rootEl = null;

  // ── Initialization ──
  function init() {
    rootEl = document.getElementById('rental-calendar-app');
    if (!rootEl) return;

    PLANS = [];
    EXTENSIONS = [{ label: '延長なし', days: 0, price: 0, variantId: null }];
    state.selectedPlanIndex = 0;
    state.selectedExtensionIndex = 0;
    state.selectedStartDate = null;
    state.selectedTimeSlot = null;
    state.timeslotOpen = false;
    state.availability = null;
    state.shipLeadDays = SHIP_LEAD_DAYS;
    state.returnBufferDays = RETURN_BUFFER_DAYS;
    state.minDate = null;
    state.maxDate = null;
    state.loading = true;
    state.error = null;
    state.validating = false;
    state.addedToCart = false;
    state.rejection = null;
    state.altData = null;
    state.prefill = readPrefillParams();

    var variantData = rootEl.dataset.variants;
    if (variantData) {
      try {
        var variants = JSON.parse(variantData);
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var days = resolveDays(v.title);
          if (days) {
            PLANS.push({ name: v.title, days: days, price: v.price, variantId: v.id });
          } else {
            console.warn('Unsupported variant title for rental plan:', v.title);
          }
        }
      } catch (e) {
        console.error('Failed to parse variant data:', e);
      }
    }

    var extData = rootEl.dataset.extensionVariants;
    if (extData) {
      try {
        var extVariants = JSON.parse(extData);
        if (extVariants.length > 0) {
          EXTENSIONS = [{ label: '延長なし', days: 0, price: 0, variantId: null }];
          extVariants.sort(function (a, b) { return a.days - b.days; });
          for (var ei = 0; ei < extVariants.length; ei++) {
            var ev = extVariants[ei];
            if (ev.days <= 0) continue;
            EXTENSIONS.push({
              label: '+' + ev.days + '日',
              days: ev.days,
              price: ev.price,
              variantId: ev.id
            });
          }
        }
      } catch (e) {
        console.error('Failed to parse extension variant data:', e);
      }
    }
    ALT_PRODUCTS = [];
    var altAttr = rootEl.dataset.altProducts;
    if (altAttr) {
      try {
        var alts = JSON.parse(altAttr);
        var selfKey = canonicalModelKey(rootEl.dataset.product || '');
        for (var an = 0; an < alts.length; an++) {
          var alt = alts[an];
          if (!alt || !alt.slug || !alt.url || !alt.label || !alt.prices) continue;
          // 自機種は候補に出さない。共有テンプレでは data-product が
          // product.handle（例: galaxy-s24-ultra-rental）になるため、
          // API側 resolveProductKey と同じ正規化を通して比較する。
          if (canonicalModelKey(alt.slug) === selfKey) continue;
          ALT_PRODUCTS.push(alt);
        }
      } catch (e) {
        console.error('Failed to parse alt product data:', e);
      }
    }

    EARLY_BIRD = parseEarlyBirdConfig(rootEl.dataset.earlyBird);

    if (PLANS.length === 0) {
      state.error = 'プラン情報の取得に失敗しました。ページを再読み込みしてください。';
      state.loading = false;
      render();
      return;
    }

    var defaultIndex = 0;
    for (var p = 0; p < PLANS.length; p++) {
      if (PLANS[p].days === 4) { defaultIndex = p; break; }
    }
    state.selectedPlanIndex = defaultIndex;

    var now = new Date();
    state.currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    render();
    fetchAvailability();
    fetchAltAvailability();
  }

  // ── API ──
  function fetchAvailability() {
    var apiUrl = rootEl.dataset.apiUrl;
    var product = rootEl.dataset.product;

    if (!apiUrl || !product) {
      state.error = 'カレンダーの設定が正しくありません';
      state.loading = false;
      render();
      return;
    }

    var url = apiUrl + '?product=' + encodeURIComponent(product) + '&format=v2' + REGION_QUERY;

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('API error: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        applyAvailabilityPayload(data);
        state.loading = false;
        state.error = null;
        if (window.TRAnalytics) {
          TRAnalytics.availabilityChecked(product);
        }
        var hadPrefill = applyPrefill();
        render();
        if (hadPrefill) openDialogForHandoff();
      })
      .catch(function (err) {
        console.error('Availability fetch error:', err);
        state.error = '予約状況の取得に失敗しました。ページを再読み込みしてください。';
        state.loading = false;
        if (window.TRAnalytics) {
          TRAnalytics.availabilityFailed(product, err.message || 'unknown');
        }
        render();
      });
  }

  // Apply an availability API payload to state. Shared by the load-time fetch
  // and the add-to-cart validate response (whose payload has the same shape).
  // v2 (NOB-1290): 日付ごとの {date, remaining, status} を受け取る。生値の
  // booked / total_inventory はもう公開されない。v1レスポンス（未移行API・
  // キャッシュ残り）も同じ内部形式に変換して受けるので、opsとテーマの
  // デプロイ順序に依存しない。
  function applyAvailabilityPayload(data) {
    state.availability = {};
    if (typeof data.ship_lead_days === 'number') state.shipLeadDays = data.ship_lead_days;
    if (typeof data.return_buffer_days === 'number') state.returnBufferDays = data.return_buffer_days;
    state.minDate = data.min_date ? parseDate(data.min_date) : null;
    state.maxDate = data.max_date ? parseDate(data.max_date) : null;

    if (data.dates && Array.isArray(data.dates)) {
      // v2: サーバ計算の remaining（キャップ済）と low 判定をそのまま採用。
      for (var i = 0; i < data.dates.length; i++) {
        var entry = data.dates[i];
        if (!entry || typeof entry.date !== 'string') continue;
        state.availability[entry.date] = {
          remaining:
            (typeof entry.remaining === 'number' && isFinite(entry.remaining))
              ? entry.remaining
              : REMAINING_CAP,
          low: entry.status === 'low'
        };
      }
    } else if (data.unavailable_dates && Array.isArray(data.unavailable_dates)) {
      // v1フォールバック: booked / total_inventory から v2 と同じ内部形式を
      // 導出する（ops側 toDateStatuses のミラー）。min〜max+バッファの全日を
      // 走査するのは、total_inventory=0（全機メンテ等）の日をもれなく full に
      // するため。totalの4フォールバックは旧実装踏襲（0は正当な値として尊重）。
      var total =
        (typeof data.total_inventory === 'number' && isFinite(data.total_inventory))
          ? data.total_inventory
          : 4;
      // 評価対象 = 受信エントリ全日 ∪ min〜max+バッファの走査窓。
      // 現行APIはエントリを窓内にクランプして返すが、窓外のエントリが来ても
      // （キャッシュ残り・将来のサーバ変更）落とさず旧実装同様ブロックに使う。
      // 窓の走査は total<4 のフリートで booked=0 の日や total=0 の全日満室を
      // もれなく載せるため。
      var counts = {};
      var toEval = {};
      for (var j = 0; j < data.unavailable_dates.length; j++) {
        var v1entry = data.unavailable_dates[j];
        if (v1entry && typeof v1entry.date === 'string') {
          counts[v1entry.date] = v1entry.booked;
          toEval[v1entry.date] = true;
        }
      }
      if (state.minDate && state.maxDate) {
        var scanEnd = addDays(state.maxDate, state.returnBufferDays);
        for (var d = new Date(state.minDate); d <= scanEnd; d = addDays(d, 1)) {
          toEval[formatDate(d)] = true;
        }
      }
      for (var key in toEval) {
        var booked = counts[key] || 0;
        var remaining = Math.max(0, total - booked);
        if (remaining >= REMAINING_CAP) continue;
        state.availability[key] = {
          remaining: remaining,
          low: remaining > 0 && remaining <= LOW_STOCK_THRESHOLD && booked > 0
        };
      }
    }

    // NOB-1261: 商品ページCTA直下の「最短◯日開始OK」表示
    // （snippets/earliest-start-note.liquid）が実在庫でゲートできるよう、
    // 最新のavailabilityペイロードを公開してイベントで通知する。
    // 時計だけで「開始OK」を断定すると満室日でもOKと出てしまうため、
    // 表示側はこのデータが無い限り何も出さない。
    window.TRAvailability = {
      dates: state.availability,
      shipLeadDays: state.shipLeadDays,
      returnBufferDays: state.returnBufferDays,
      minDate: state.minDate ? formatDate(state.minDate) : null,
      maxDate: state.maxDate ? formatDate(state.maxDate) : null
    };
    try {
      document.dispatchEvent(new CustomEvent('tr:availability-updated'));
    } catch (e) {
      /* CustomEvent非対応環境では通知なし（表示側は非表示のまま） */
    }
  }

  // Server-side re-check of the selected range at add-to-cart time (NOB-1117).
  // The availability snapshot is fetched once at page load and can be stale
  // (long-open tab, concurrent orders, fleet changes); this asks the API for a
  // fresh verdict on the exact range. Fail-open by design: a network error, a
  // non-JSON response, or a response without `available` (older API version)
  // lets the order through — the storefront must not lose sales to infra
  // hiccups, and the ops-side detection covers the residue.
  function validateRangeServer(startDate, endDate, callback) {
    var apiUrl = rootEl.dataset.apiUrl;
    var product = rootEl.dataset.product;
    if (!apiUrl || !product) {
      callback({ ok: true });
      return;
    }

    var url = apiUrl + '?product=' + encodeURIComponent(product) +
      '&validate_start=' + encodeURIComponent(formatDate(startDate)) +
      '&validate_end=' + encodeURIComponent(formatDate(endDate)) +
      '&format=v2' + REGION_QUERY;

    // `validated` はサーバ検証が実際に成立したかのフラグ（NOB-1385）。
    // fail-open で通した経路（設定欠落・通信断・旧API形状）は ok でも
    // validated ではない — カートに刻む _validated_at の根拠に使うため、
    // 「通した」と「確かめた」を区別する（レビュー指摘・CodeRabbit）。
    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('API error: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (data && data.available === false) {
          callback({ ok: false, data: data });
        } else {
          callback({ ok: true, validated: !!(data && data.available === true) });
        }
      })
      .catch(function (err) {
        console.error('Validate fetch error:', err);
        callback({ ok: true, validated: false });
      });
  }

  // ── Alternative products (NOB-1123) ──

  // availability API の resolveProductKey と同じ正規化（handle → canonical key）。
  function canonicalModelKey(slug) {
    var m = String(slug).match(/galaxy-s(\d+)-ultra/i);
    return m ? 'galaxy-s' + m[1].toLowerCase() + '-ultra' : String(slug);
  }

  // 代替機種の空きを ?products= の複数商品モードで並行取得する。
  // 失敗はすべて黙って握りつぶす — 提案チップが出ないだけで、
  // 自機種のカレンダー表示・予約フローには一切影響させない。
  function fetchAltAvailability() {
    var apiUrl = rootEl.dataset.apiUrl;
    if (!apiUrl || ALT_PRODUCTS.length === 0) return;
    var slugs = [];
    for (var i = 0; i < ALT_PRODUCTS.length; i++) slugs.push(ALT_PRODUCTS[i].slug);

    // format=v2 はサーバ側で受理して無視される（v1形状は廃止済み・NOB-1290）が、
    // 自機種リクエストと形を揃えておく（レビュー指摘への防御的対応）。
    fetch(apiUrl + '?products=' + encodeURIComponent(slugs.join(',')) + '&format=v2' + REGION_QUERY)
      .then(function (res) {
        if (!res.ok) throw new Error('API error: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.results) return;
        var bySlug = {};
        for (var s = 0; s < slugs.length; s++) {
          var r = data.results[slugs[s]];
          if (!r || r.error || !r.dates || !Array.isArray(r.dates)) continue;
          var avail = {};
          for (var j = 0; j < r.dates.length; j++) {
            var entry = r.dates[j];
            if (entry && typeof entry.date === 'string' &&
                typeof entry.remaining === 'number' && isFinite(entry.remaining)) {
              avail[entry.date] = entry.remaining;
            }
          }
          bySlug[slugs[s]] = {
            availability: avail,
            shipLeadDays: typeof r.ship_lead_days === 'number' ? r.ship_lead_days : SHIP_LEAD_DAYS,
            returnBufferDays: typeof r.return_buffer_days === 'number' ? r.return_buffer_days : RETURN_BUFFER_DAYS,
            minDate: r.min_date ? parseDate(r.min_date) : null,
            maxDate: r.max_date ? parseDate(r.max_date) : null
          };
        }
        state.altData = bySlug;
        // 却下バナーが既に出ているなら、遅れて届いた候補チップを反映する
        if (state.rejection) {
          reportAlternativeShown();
          render();
        }
      })
      .catch(function () {
        /* 提案なしで続行（fail-silent） */
      });
  }

  // 代替機種で同じ開始日・同じ日数が取れるか。データが無い機種は false
  //（「取れるかもしれない」で送客しない — 遷移先で満室は最悪の体験）。
  // ペイロードに無い日付は v2 契約どおり REMAINING_CAP 台以上の空きとして扱う。
  function altRangeAvailable(alt, startDate, totalDays) {
    var ad = state.altData && state.altData[alt.slug];
    if (!ad) return false;
    if (ad.minDate && startDate < ad.minDate) return false;
    if (ad.maxDate && startDate > ad.maxDate) return false;
    for (var i = -ad.shipLeadDays; i < totalDays + ad.returnBufferDays; i++) {
      var rem = ad.availability[formatDate(addDays(startDate, i))];
      if (typeof rem === 'number' && rem === 0) return false;
    }
    return true;
  }

  // ── Date handoff (NOB-1123) ──

  // 別機種チップからの着地パラメータを読む。ここでは読むだけで、URLからの
  // 削除は applyPrefill（availability取得成功後）で行う — 取得が一時失敗した
  // ときにパラメータを残し、リロードで引き継ぎを再試行できるようにするため
  //（レビュー指摘・Codex P2）。
  function readPrefillParams() {
    if (typeof URLSearchParams === 'undefined') return null;
    try {
      var sp = new URLSearchParams(window.location.search);
      var start = sp.get('tr_start');
      if (!start || !/^\d{4}-\d{2}-\d{2}$/.test(start)) return null;
      var days = parseInt(sp.get('tr_days'), 10);
      return { start: start, days: isNaN(days) ? null : days };
    } catch (e) {
      return null;
    }
  }

  // 引き継ぎパラメータをURLから消す（適用済みの再発火・URL共有での発火防止）。
  function stripPrefillParams() {
    if (typeof URLSearchParams === 'undefined') return;
    try {
      var sp = new URLSearchParams(window.location.search);
      if (!sp.has('tr_start') && !sp.has('tr_days')) return;
      sp.delete('tr_start');
      sp.delete('tr_days');
      var qs = sp.toString();
      history.replaceState(null, '',
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    } catch (e) {
      /* URL整理に失敗しても引き継ぎ自体は続行する */
    }
  }

  // availability 取得後に引き継ぎ日程を適用する。送客元の表示から
  // キャッシュTTL（最大5分）ぶんズレて埋まっていた場合は、通常の
  // 却下バナーがそのまま理由を説明する（嘘の「空きあり」で終わらせない）。
  function applyPrefill() {
    var p = state.prefill;
    if (!p) return false;
    state.prefill = null;
    // ここまで来た＝availability取得成功＝引き継ぎを処理できるので、
    // この時点で初めてURLからパラメータを消す（取得失敗時は残る）。
    stripPrefillParams();

    if (p.days) {
      for (var i = 0; i < PLANS.length; i++) {
        if (PLANS[i].days === p.days) {
          state.selectedPlanIndex = i;
          break;
        }
      }
    }

    var d = parseDate(p.start);
    if (!isDateDisabled(d) && isRangeAvailable(d, getTotalDays())) {
      state.currentMonth = new Date(d.getFullYear(), d.getMonth(), 1);
      state.selectedStartDate = d;
      state.rejection = null;
      state.slotNudge = true; // 引き継ぎ着地でも残る操作は時間帯だけ
      if (window.TRAnalytics && TRAnalytics.datePrefilled) {
        TRAnalytics.datePrefilled(p.start, 'applied');
      }
    } else {
      var reason = disabledReason(d);
      state.selectedStartDate = null;
      // past/disabled はタップ時（onRejectedTap）と同じく沈黙させる —
      // 古いURLで着地した時に別理由の嘘メッセージを出さない（レビュー指摘）。
      // 月も動かさない（過去日の月へ黙って移動すると説明のないまま
      // 過去のカレンダーに着地してしまう・レビュー指摘）。
      if (reason !== 'past' && reason !== 'disabled') {
        state.currentMonth = new Date(d.getFullYear(), d.getMonth(), 1);
        state.rejection = { dateStr: p.start, reason: reason, altShownFired: false };
        reportAlternativeShown();
      }
      if (window.TRAnalytics && TRAnalytics.datePrefilled) {
        TRAnalytics.datePrefilled(p.start, reason);
      }
    }
    return true;
  }

  function openDialogForHandoff() {
    var dialog = document.getElementById('rental-calendar-dialog');
    if (!dialog || !dialog.showModal || dialog.open) return;
    dialog.showModal();
    if (window.TRAnalytics) {
      TRAnalytics.calendarOpened(!!state.availability, 'alt_handoff');
    }
    reportEarlyBird('shown', null);
  }

  // ── Date utilities ──
  function formatDate(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function formatMD(d) {
    return (d.getMonth() + 1) + '/' + d.getDate();
  }

  function formatMDW(d) {
    return formatMD(d) + '(' + WEEKDAY_LABELS[d.getDay()] + ')';
  }

  function formatDateKanji(d) {
    // YYYY.MM.DD（ゼロ埋め）— 顧客向け表示用。webhookは _start_iso8601 を優先参照、
    // フォールバックの normalizeDate もドット形式に対応済みなので安全。
    var mm = ('0' + (d.getMonth() + 1)).slice(-2);
    var dd = ('0' + d.getDate()).slice(-2);
    return d.getFullYear() + '.' + mm + '.' + dd;
  }

  function parseDate(str) {
    var parts = str.split('-');
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  }

  function addDays(d, n) {
    var result = new Date(d);
    result.setDate(result.getDate() + n);
    return result;
  }

  function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
  }

  // 現在のJST壁時計を「UTCゲッターで読むDate」として返す。
  // getTimezoneOffset()でローカル→UTC変換してからローカルゲッターで読む方式は、
  // 閲覧環境がDSTを持つ場合に切替時刻付近で1時間ズレるため使わない
  // （assets/cart-deadline-guard.js と同じパターン）。
  function nowJST() {
    return new Date(Date.now() + 9 * 60 * 60 * 1000);
  }

  function todayJST(jstNow) {
    var jst = jstNow || nowJST();
    return new Date(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate());
  }

  function effectiveLeadDays(jstNow) {
    var jst = jstNow || nowJST();
    return jst.getUTCHours() >= LATE_CUTOFF_HOUR_JST
      ? LEAD_TIME_DAYS_BASE + 1
      : LEAD_TIME_DAYS_BASE;
  }

  function earliestStartDate() {
    var jst = nowJST();
    return addDays(todayJST(jst), effectiveLeadDays(jst));
  }

  // ── Availability check ──
  // ペイロードに無い日付は REMAINING_CAP(4)台以上空いている（v2の契約）。
  // availability未取得（API失敗）の場合も同じ扱い＝fail-open。実際の枠は
  // カート追加時のサーバ再検証（validateRangeServer）が最終防衛線。
  function getDateInfo(dateStr) {
    return (state.availability && state.availability[dateStr]) || null;
  }

  function getRemainingCount(dateStr) {
    var info = getDateInfo(dateStr);
    return info ? info.remaining : REMAINING_CAP;
  }

  function isDateFullyBooked(dateStr) {
    return getRemainingCount(dateStr) === 0;
  }

  function isDateDisabled(d) {
    var today = todayJST();
    if (d < today) return true;
    var earliest = earliestStartDate();
    if (d < earliest) return true;
    // Cap to the availability window. The 6-week grid renders adjacent-month
    // spillover days; without this, days past max_date (absent from the
    // payload, so treated as 0 booked) would be selectable outside the
    // intended booking/navigation window.
    if (state.minDate && d < state.minDate) return true;
    if (state.maxDate && d > state.maxDate) return true;
    var dateStr = formatDate(d);
    if (isDateFullyBooked(dateStr)) return true;
    return false;
  }

  // Why a rendered day is unselectable. Mirrors the render's `clickable`
  // condition (!isDateDisabled && isRangeAvailable) in priority order, so GA4
  // can distinguish friction types on a greyed-out date tap. The range check
  // is last because a day can be both individually free yet window-blocked —
  // 'range_unavailable' (start is free but the ship/rental/return window
  // overlaps a booked day) is the most actionable signal and must not fall
  // through to the generic 'disabled'.
  function disabledReason(d) {
    if (d < todayJST()) return 'past';
    if (d < earliestStartDate()) return 'too_soon';
    if (state.minDate && d < state.minDate) return 'out_of_window';
    if (state.maxDate && d > state.maxDate) return 'out_of_window';
    if (isDateFullyBooked(formatDate(d))) return 'booked';
    if (!isRangeAvailable(d, getTotalDays())) return 'range_unavailable';
    return 'disabled';
  }

  // 開始日までの日数。date_selected と同じ算出式を使い、カート側イベントと
  // 突き合わせられるようにする（月送り改善の効果判定用）。
  function leadDaysFrom(startDate) {
    if (!startDate) return null;
    return Math.round((startDate - todayJST()) / 86400000);
  }

  // ── 早割（NOB-1616）: 純関数。tests/early-bird.test.js が node で検証する ──
  // state を読まず、today / config を引数で受ける（規則は ops 側 early-bird.ts と同じ）。

  // data-early-bird の JSON → {minLeadDays, percent}。無い・壊れている・範囲外は null（早割なし）。
  function parseEarlyBirdConfig(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    var obj = raw;
    if (typeof raw === 'string') {
      try { obj = JSON.parse(raw); } catch (e) { return null; }
    }
    if (!obj || typeof obj !== 'object') return null;
    var minLeadDays = Number(obj.minLeadDays);
    var percent = Number(obj.percent);
    if (!isFinite(minLeadDays) || minLeadDays !== Math.floor(minLeadDays) ||
        minLeadDays < 0 || minLeadDays > 365) return null;
    if (!isFinite(percent) || percent <= 0 || percent > 100) return null;
    return { minLeadDays: minLeadDays, percent: percent };
  }

  // 開始日までの暦日数（開始日 − 今日）。leadDaysFrom() と同じ式で today を外から渡せる形。
  function leadDaysBetween(startDate, today) {
    return Math.round((startDate - today) / 86400000);
  }

  // その日数で早割が付くか。決済時点の判定（関数側）を「今日」で先取りした表示用。
  function isEarlyBirdLead(leadDays, config) {
    return !!config && typeof leadDays === 'number' && leadDays >= config.minLeadDays;
  }

  // 早割の最初の対象日（今日 + minLeadDays）。空きは見ない（境目「早割はここから」の位置）。
  function firstEarlyBirdDate(today, config) {
    if (!config) return null;
    return addDays(today, config.minLeadDays);
  }

  // 最寄りの「選べる」対象日。isSelectable(date) は呼び出し側の締切・空き判定。
  // maxDate（受付窓の末尾）を超えたら null。maxDate 不明なら 60 日で安全停止。
  function nearestEarlyBirdDate(today, config, isSelectable, maxDate) {
    var first = firstEarlyBirdDate(today, config);
    if (!first) return null;
    var limit = maxDate || addDays(first, 60);
    for (var d = new Date(first); d <= limit; d = addDays(d, 1)) {
      if (isSelectable(d)) return d;
    }
    return null;
  }

  // 割引額（円）。関数側は行ごと（本体・延長行）に % を掛けて通貨の最小単位に丸めるので、
  // ここも行ごとに丸めて足す（表示と実額のズレを作らない）。
  function earlyBirdAmount(price, percent) {
    return Math.round(price * percent / 100);
  }

  // 表示用の価格一式。対象外なら discount=0・total は元のまま。config 無しも同じ。
  function earlyBirdPricing(planPrice, extPrice, leadDays, config) {
    var eligible = isEarlyBirdLead(leadDays, config);
    var planOff = eligible ? earlyBirdAmount(planPrice, config.percent) : 0;
    var extOff = eligible ? earlyBirdAmount(extPrice, config.percent) : 0;
    return {
      eligible: eligible,
      percent: config ? config.percent : null,
      planPrice: planPrice,
      planDiscounted: planPrice - planOff,
      extPrice: extPrice,
      extDiscounted: extPrice - extOff,
      discount: planOff + extOff,
      total: planPrice + extPrice - planOff - extOff
    };
  }

  // 10 → "10"、12.5 → "12.5"（小数点以下の無駄なゼロを出さない）。
  function formatPercent(percent) {
    return String(Number(percent.toFixed(2)));
  }

  // 凡例・下部バーの共通ラベル。関数側の割引名（discountMessage）と同じ語彙。
  function earlyBirdLabel(config) {
    return '早割 ' + formatPercent(config.percent) + '%オフ';
  }

  // 対象外の日を選んだ時のひと押し（1行・最寄りの対象日を実日付で）。
  function earlyBirdNudgeText(date, config) {
    return formatMD(date) + '以降の開始なら' + formatPercent(config.percent) + '%オフ';
  }

  // 決済時点で判定される旨の注記（カート滞留で日をまたぐと消えるため）。
  // 「決済時点」は固いので「お支払い」で言う（2026-10-17・Grok 指摘）。1 行に収まる長さを保つ
  // （359px 以下の幅だけ CSS が 2 行目に送る。文言を伸ばすなら 375px で 1 行に入るか実測する）。
  function earlyBirdNoteText(config) {
    return 'お支払いが開始日の' + config.minLeadDays + '日以上前なら適用';
  }

  // 画面に出している割引後価格が古くなったか。描画時は対象だった開始日が、モーダルを開いたまま
  // JST の日付が変わって対象外になった時だけ true（決済では関数が引かないので、割引後の価格を
  // 見せたまま進めない・レビュー指摘 Codex P2）。描画時に対象外だった場合は常に false
  // （日が進んでも対象外→対象には戻らない）。
  function earlyBirdDisplayStale(shownEligible, leadDays, config) {
    return !!shownEligible && !isEarlyBirdLead(leadDays, config);
  }

  // ── 早割: ウィジェット状態を読む側 ──
  // 表示した割引後価格が日付変更で古くなった時の共通応答: 元の価格に描き直して知らせ、進めない
  // （もう一度押せば通常価格で進める）。
  function rejectStaleEarlyBird() {
    render();
    alert('日付が変わったため、選択中の開始日は早割の対象外になりました。表示の料金をご確認のうえ、もう一度お進みください。');
  }

  function currentEarlyBird() {
    var lead = state.selectedStartDate ? leadDaysFrom(state.selectedStartDate) : null;
    return earlyBirdPricing(getSelectedPlan().price, getSelectedExtension().price, lead, EARLY_BIRD);
  }

  function earlyBirdNudgeDate() {
    if (!EARLY_BIRD) return null;
    var totalDays = getTotalDays();
    return nearestEarlyBirdDate(todayJST(), EARLY_BIRD, function (d) {
      return !isDateDisabled(d) && isRangeAvailable(d, totalDays);
    }, state.maxDate);
  }

  function reportEarlyBird(action, leadDays) {
    if (!EARLY_BIRD || !window.TRAnalytics || !TRAnalytics.earlyBird) return;
    TRAnalytics.earlyBird(action, EARLY_BIRD.minLeadDays, EARLY_BIRD.percent, leadDays);
  }

  // How far the user advanced in the calendar before closing it.
  function currentFunnelStep() {
    if (state.addedToCart) return 'added';
    if (state.selectedTimeSlot) return 'timeslot';
    if (state.selectedStartDate) return 'date';
    return 'browse';
  }

  function isRangeAvailable(startDate, totalDays) {
    var shipLead = state.shipLeadDays;
    var returnBuf = state.returnBufferDays;
    for (var i = -shipLead; i < totalDays + returnBuf; i++) {
      var d = addDays(startDate, i);
      var dateStr = formatDate(d);
      if (isDateFullyBooked(dateStr)) return false;
    }
    return true;
  }

  // 「残りわずか」判定はサーバ側（残り3台以下かつ予約あり、NOB-1290）。
  // クライアントはしきい値を持たない＝方針変更時はops側デプロイのみで済む。
  function isLowStock(dateStr) {
    var info = getDateInfo(dateStr);
    return !!(info && info.low);
  }

  // ── Rejection response (NOB-1123) ──
  // グレー日タップへの応答。メッセージは disabledReason() が判定した事実のみ、
  // チップ（次の一手）は最大2個。「今予約すると」型のアンカー表現は使わない。
  function buildRejection(d, reason) {
    var chips = [];
    var plan = getSelectedPlan();
    var totalDays = getTotalDays();

    if (reason === 'out_of_window' && state.maxDate && d > state.maxDate) {
      return {
        message: formatMDW(d) + '開始分の受付はまだ始まっていません（現在 ' +
          formatMD(state.maxDate) + ' 開始分まで受付中）',
        chips: chips
      };
    }

    if (reason === 'too_soon' || reason === 'out_of_window') {
      // 機種チップは出さない — 機種を変えても締切は同じで、提案が嘘になる。
      var e0 = findEarliestSelectable();
      if (e0) {
        chips.push({
          kind: 'earliest',
          dateStr: formatDate(e0),
          label: '最短 ' + formatMDW(e0) + ' 開始で予約する'
        });
      }
      // 文言は1行で確定させる（2行化はPC表示崩れの一因・2026-08-08）。
      // 締切ルールの詳細（2日前15時）は説明せず、次の一手（最短日チップ）に任せる。
      return {
        message: formatMDW(d) + '開始は締め切りました',
        chips: chips
      };
    }

    // booked / range_unavailable 共通。「満室」はホテル語なので使わず、
    // 凡例・チップと同じ「空き」の語彙で統一する（ノブヒロ決定 2026-08-08）。
    // range の理屈（期間の途中に空きゼロ日がある）は顧客には無意味なので説明しない。
    var message = formatMDW(d) + '開始は空きがありません';

    // 別機種チップ: 同じ開始日・同じ日数で窓全体が空いている候補、価格が近い順。
    // 延長選択中は出さない（レビュー指摘・Codex P2）— 引き継ぎは基本プランしか
    // 運べないため、延長込みの希望を黙って短縮し価格も過小表示することになる。
    var altChips = [];
    if (getSelectedExtension().days === 0) {
      for (var i = 0; i < ALT_PRODUCTS.length; i++) {
        var alt = ALT_PRODUCTS[i];
        var price = alt.prices[String(plan.days)];
        if (typeof price !== 'number') continue;
        if (!altRangeAvailable(alt, d, totalDays)) continue;
        altChips.push({
          kind: 'alt',
          slug: alt.slug,
          href: alt.url + (alt.url.indexOf('?') === -1 ? '?' : '&') +
            'tr_start=' + formatDate(d) + '&tr_days=' + plan.days,
          label: alt.label + 'なら空きあり ' + formatPrice(price),
          priceDelta: Math.abs(price - plan.price)
        });
      }
      altChips.sort(function (a, b) { return a.priceDelta - b.priceDelta; });
    }

    // range_unavailable でも booked と同じく別機種チップのみ（ノブヒロ判断
    // 2026-08-07）。「短いプランなら取れます」の能動提案はしない — 実需要より
    // 短い貸出をゼロ余裕の窓に作ると、レンタル中の「延長したい」を構造的に
    // 断れない場所（後ろが詰まっているからこそ短プランになった）で製造し、
    // 断られた顧客の延滞がバッファを食って次の発送を飛ばすリスクになるため。
    // 顧客が自発的にプランを切り替えて取る経路は従来どおり残る。
    chips = altChips.slice(0, 2);
    // 全提案が不成立ならメッセージのみ。「次に空きがある日へ」チップは
    // 意図的に入れない — 日付をずらす提案は離脱の口実になる（2026-08-07判断）。
    return { message: message, chips: chips };
  }

  // 最短の選択可能開始日。締切だけでなく在庫ブロックも見る。受付窓
  //（maxDate）の末尾まで探索する — 直近が全ブロックでも後ろに空きがあれば
  // 案内できるように（レビュー指摘・Codex P2）。maxDate不明時のみ60日で
  // 安全停止する。
  function findEarliestSelectable() {
    var base = earliestStartDate();
    var i = 0;
    while (true) {
      var c = addDays(base, i);
      if (state.maxDate) {
        if (c > state.maxDate) return null;
      } else if (i >= 60) {
        return null;
      }
      if (!isDateDisabled(c) && isRangeAvailable(c, getTotalDays())) return c;
      i++;
    }
  }

  // ── Computed values ──
  function getSelectedPlan() {
    return PLANS[state.selectedPlanIndex];
  }

  function getSelectedExtension() {
    return EXTENSIONS[state.selectedExtensionIndex];
  }

  function getTotalDays() {
    return getSelectedPlan().days + getSelectedExtension().days;
  }

  function getEndDate() {
    if (!state.selectedStartDate) return null;
    return addDays(state.selectedStartDate, getTotalDays() - 1);
  }

  function getSlotLabel() {
    if (!state.selectedTimeSlot) return null;
    for (var s = 0; s < TIME_SLOTS.length; s++) {
      if (TIME_SLOTS[s].value === state.selectedTimeSlot) return TIME_SLOTS[s].label;
    }
    return null;
  }

  function formatPrice(n) {
    return '¥' + n.toLocaleString('ja-JP');
  }

  // ── Event handlers ──
  function onPlanSelect(index) {
    state.selectedPlanIndex = index;
    state.selectedExtensionIndex = 0;
    state.rejection = null;
    var plan = PLANS[index];
    if (plan && window.TRAnalytics) {
      TRAnalytics.planSelected(plan.name, plan.price, plan.days);
    }
    if (state.selectedStartDate && !isRangeAvailable(state.selectedStartDate, getTotalDays())) {
      state.selectedStartDate = null;
    }
    render();
  }

  function onExtensionSelect(index) {
    state.selectedExtensionIndex = index;
    state.rejection = null;
    var ext = EXTENSIONS[index];
    if (ext && window.TRAnalytics) {
      TRAnalytics.extensionSelected(ext.label, ext.price, ext.days);
    }
    if (state.selectedStartDate && !isRangeAvailable(state.selectedStartDate, getTotalDays())) {
      state.selectedStartDate = null;
    }
    render();
  }

  function onDayClick(dateStr) {
    var d = parseDate(dateStr);
    if (isDateDisabled(d)) return;
    if (!isRangeAvailable(d, getTotalDays())) {
      if (window.TRAnalytics) {
        TRAnalytics.dateUnavailable(dateStr, 'range_unavailable');
      }
      return;
    }
    state.selectedStartDate = d;
    state.rejection = null;
    state.slotNudge = !state.selectedTimeSlot; // 次の一手（時間帯）へ視線誘導
    // Keep the visible month in sync when an adjacent-month day is tapped.
    state.currentMonth = new Date(d.getFullYear(), d.getMonth(), 1);
    var endDate = addDays(d, getTotalDays() - 1);
    if (window.TRAnalytics) {
      var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      var leadDays = leadDaysFrom(d);
      TRAnalytics.dateSelected(
        dateStr,
        formatDate(endDate),
        getTotalDays(),
        leadDays,
        DOW[d.getDay()]
      );
    }
    if (isEarlyBirdLead(leadDaysFrom(d), EARLY_BIRD)) reportEarlyBird('date_selected', leadDaysFrom(d));
    render();
  }

  // 「M/D以降の開始なら◯%オフ」（NOB-1616）: 最寄りの対象日へ移る。
  function onEarlyBirdNudge(dateStr) {
    reportEarlyBird('nudge_clicked', leadDaysFrom(parseDate(dateStr)));
    onDayClick(dateStr);
  }

  // グレー日タップへの応答（NOB-1123）。従来の無反応をやめ、理由と次の一手を
  // お届け/返却行の位置に表示する。past だけは従来どおり沈黙（説明がノイズ）。
  function onRejectedTap(dateStr, reason) {
    if (reason === 'past' || reason === 'disabled') return;
    var d = parseDate(dateStr);
    // タップ＝「この日が希望」の表明として扱い、既存の選択はクリアする
    //（プラン変更で不成立になった選択をクリアする既存挙動と同じ考え方）。
    state.selectedStartDate = null;
    state.rejection = { dateStr: dateStr, reason: reason, altShownFired: false };
    state.currentMonth = new Date(d.getFullYear(), d.getMonth(), 1);
    reportAlternativeShown();
    render();
  }

  // alternative_shown はバナー1回につき1回だけ送る。候補データが遅れて
  // 届いた場合（fetchAltAvailability完了時）もここを通る。
  function reportAlternativeShown() {
    if (!state.rejection || state.rejection.altShownFired) return;
    var built = buildRejection(parseDate(state.rejection.dateStr), state.rejection.reason);
    var altSlugs = [];
    for (var i = 0; i < built.chips.length; i++) {
      if (built.chips[i].kind === 'alt') altSlugs.push(built.chips[i].slug);
    }
    if (altSlugs.length && window.TRAnalytics && TRAnalytics.alternativeShown) {
      TRAnalytics.alternativeShown(state.rejection.reason, state.rejection.dateStr, altSlugs);
      state.rejection.altShownFired = true;
    }
  }

  function onPrevMonth() {
    state.currentMonth = new Date(
      state.currentMonth.getFullYear(),
      state.currentMonth.getMonth() - 1,
      1
    );
    reportMonthNav('prev');
    render();
  }

  function onNextMonth() {
    state.currentMonth = new Date(
      state.currentMonth.getFullYear(),
      state.currentMonth.getMonth() + 1,
      1
    );
    reportMonthNav('next');
    render();
  }

  function reportMonthNav(direction) {
    if (!window.TRAnalytics) return;
    var now = todayJST();
    var monthsFromNow =
      (state.currentMonth.getFullYear() - now.getFullYear()) * 12 +
      (state.currentMonth.getMonth() - now.getMonth());
    var month =
      state.currentMonth.getFullYear() +
      '-' +
      ('0' + (state.currentMonth.getMonth() + 1)).slice(-2);
    TRAnalytics.monthNavigated(direction, month, monthsFromNow);
  }

  function onTimeSlotSelect(value) {
    state.selectedTimeSlot = value;
    state.timeslotOpen = false;
    if (window.TRAnalytics) {
      TRAnalytics.timeslotSelected(value);
    }
    render();
  }

  function onOpenSlot() {
    state.timeslotOpen = true;
    render();
  }

  function onCloseSlot() {
    state.timeslotOpen = false;
    render();
  }

  function onCta() {
    // Progressive CTA: guide the user to the next missing step.
    if (!state.selectedStartDate) {
      if (window.TRAnalytics) TRAnalytics.ctaBlocked('no_date');
      return;
    }
    if (!state.selectedTimeSlot) {
      // シートが正常に開く導線なので「詰まり」ではない（専用イベント）
      if (window.TRAnalytics && TRAnalytics.progressiveCtaClicked) {
        TRAnalytics.progressiveCtaClicked();
      }
      state.timeslotOpen = true;
      render();
      return;
    }
    onAddToCart();
  }

  function closeDialog() {
    var dialog = document.getElementById('rental-calendar-dialog');
    if (dialog && dialog.close) dialog.close();
  }

  function onAddToCart() {
    if (state.validating) return;
    if (!state.selectedStartDate || !state.selectedTimeSlot) return;

    var earliest = earliestStartDate();
    if (state.selectedStartDate < earliest) {
      state.selectedStartDate = null;
      render();
      alert('選択されていた開始日は発送リードタイムを過ぎたため無効になりました。新しい日付を選択してください。');
      return;
    }

    // 早割（NOB-1616）: 表示時は対象だった開始日が、モーダルを開いたまま JST の日付が変わって
    // 対象外になっていたら、割引後の価格を見せたまま進めない（決済時点で判定する関数は引かない）。
    // 元の価格に描き直して知らせ、もう一度押してもらう（上の締切チェックと同じ流儀・Codex P2）。
    if (earlyBirdDisplayStale(state.earlyBirdShown, leadDaysFrom(state.selectedStartDate), EARLY_BIRD)) {
      rejectStaleEarlyBird();
      return;
    }

    var plan = getSelectedPlan();
    var ext = getSelectedExtension();

    if (window.TRAnalytics) {
      TRAnalytics.addToCartClicked(
        plan.name,
        plan.price + ext.price,
        formatDate(state.selectedStartDate),
        formatDate(getEndDate()),
        leadDaysFrom(state.selectedStartDate)
      );
    }

    // Freeze what is being validated: while the request is in flight only the
    // CTA is disabled, so date/plan/slot controls can still mutate state. The
    // cart must never receive a selection the server did not see.
    var snapshot = {
      start: state.selectedStartDate,
      planIndex: state.selectedPlanIndex,
      extensionIndex: state.selectedExtensionIndex,
      timeSlot: state.selectedTimeSlot
    };

    var btn = rootEl.querySelector('.rc-cta');
    if (btn) {
      btn.disabled = true;
      btn.textContent = '空きを確認中...';
    }

    state.validating = true;
    validateRangeServer(snapshot.start, getEndDate(), function (verdict) {
      state.validating = false;

      if (state.selectedStartDate !== snapshot.start ||
          state.selectedPlanIndex !== snapshot.planIndex ||
          state.selectedExtensionIndex !== snapshot.extensionIndex ||
          state.selectedTimeSlot !== snapshot.timeSlot) {
        // Selection changed mid-flight: the verdict no longer matches what the
        // user sees. Rebuild the UI (re-enabling the CTA) and let them submit
        // the new selection again.
        render();
        return;
      }

      // 早割（NOB-1616）: サーバ検証の往復中に JST の日付が変わり、見せていた割引が
      // 対象外になった場合も進めない（押下前の再確認と同じ応答・Codex P2）。
      if (earlyBirdDisplayStale(state.earlyBirdShown, leadDaysFrom(snapshot.start), EARLY_BIRD)) {
        rejectStaleEarlyBird();
        return;
      }

      if (verdict.ok) {
        submitToCart(plan, ext, verdict.validated === true);
        return;
      }
      // The range was taken (or expired) while the calendar was open.
      // Refresh counts from the validate payload, clear the selection, and
      // let the user re-pick. render() rebuilds the CTA, resetting the button.
      if (window.TRAnalytics) TRAnalytics.ctaBlocked('range_taken');
      applyAvailabilityPayload(verdict.data || {});
      state.selectedStartDate = null;
      state.timeslotOpen = false;
      render();
      alert(verdict.data && verdict.data.reason === 'too_soon'
        ? '選択されていた開始日は発送リードタイムを過ぎたため無効になりました。新しい日付を選択してください。'
        : '申し訳ありません。この日程は先ほど埋まりました。お手数ですが別の日程をお選びください。');
    });
  }

  function submitToCart(plan, ext, serverValidated) {
    var startDate = state.selectedStartDate;
    var endDate = getEndDate();

    // ISO8601 UTC: start is previous day 15:00 UTC (= midnight JST)
    var startUTC = new Date(Date.UTC(
      startDate.getFullYear(), startDate.getMonth(), startDate.getDate() - 1, 15, 0, 0
    ));
    // finish is end date 14:59:59 UTC (= 23:59:59 JST)
    var finishUTC = new Date(Date.UTC(
      endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 14, 59, 59
    ));

    var variantId = plan.variantId;
    if (!variantId) {
      alert('バリアントの選択に失敗しました。ページを再読み込みしてください。');
      return;
    }

    var timeSlotLabel = getSlotLabel() || '';
    var productTitle = rootEl.dataset.productTitle || '';

    // Customer-visible line item properties (clean, no duplicates)
    var startKanji = formatDateKanji(startDate);
    var endKanji = formatDateKanji(endDate);

    // NOB-1385: サーバ側在庫検証(validateRangeServer)を「実際に通過した」
    // 時刻。カート層(cart-deadline-guard.js)がこの鮮度を見て、古い明細だけ
    // 決済前に在庫を再検証する（カート滞留すり抜け対策）。fail-open で
    // 通しただけ（通信断・設定欠落）の明細には刻まない — 未検証の明細を
    // 30分間 fresh 扱いにしないため（レビュー指摘・CodeRabbit）。
    // 刻印なしはカート層で「古い」扱い＝安全側。
    var validatedAt = serverValidated ? new Date().toISOString() : null;

    var mainProperties = {
      'レンタル開始日': startKanji,
      'レンタル終了日': endKanji,
      '配達時間帯': timeSlotLabel,
      '_start_iso8601': startUTC.toISOString(),
      '_finish_iso8601': finishUTC.toISOString()
    };
    if (validatedAt) mainProperties['_validated_at'] = validatedAt;

    // Extension: visible label + hidden machine-readable value
    // NOTE: 本体の `_extension_days` と延長行の `_parent_variant_id` / `_start_iso8601`
    // は、カート層の assets/cart-extension-sync.js（延長行だけ削除された時に本体を
    // 延長なしへ書き戻す）と TechRental-ops の extension-guard.ts が対応づけに使う。
    // キー名や形式を変える時は両方を同時に直すこと。
    if (ext.days > 0) {
      mainProperties['延長'] = '+' + ext.days + '日延長';
      mainProperties['_extension_days'] = String(ext.days);
    }

    var items = [
      {
        id: parseInt(variantId, 10),
        quantity: 1,
        properties: mainProperties
      }
    ];

    if (ext.days > 0 && ext.variantId) {
      var extProperties = {
        '_is_extension': 'true',
        '_parent_variant_id': String(variantId),
        '_parent_product_title': productTitle,
        '_start_iso8601': startUTC.toISOString(),
        '_finish_iso8601': finishUTC.toISOString(),
        '_extension_days': String(ext.days)
      };
      if (validatedAt) extProperties['_validated_at'] = validatedAt;
      // 延長を先に送る: Shopify のカートは 1 回のリクエストで入れた明細を後ろから並べる
      // （[本体, 延長] で入れると延長の行が本体より上に出て、決済画面も同じ順になる・
      // 2026-10-23 実測）。本体を上に出したいので逆順で送る。並び順に頼る処理は無い
      // （cart-extension-sync.js / cart-deadline-guard.js はプロパティで照合、ops の
      // extension-guard.ts は本体と延長を別々に並べて消費する）。
      items.unshift({
        id: parseInt(ext.variantId, 10),
        quantity: 1,
        properties: extProperties
      });
    }

    var btn = rootEl.querySelector('.rc-cta');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'カートに追加中...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error(data.description || 'カート追加に失敗しました');
          });
        }
        return res.json();
      })
      .then(function () {
        state.addedToCart = true;
        if (window.TRAnalytics) {
          TRAnalytics.addToCartSuccess(
            plan.name, plan.price + ext.price, leadDaysFrom(startDate)
          );
        }
        if (isEarlyBirdLead(leadDaysFrom(startDate), EARLY_BIRD)) {
          reportEarlyBird('add_to_cart', leadDaysFrom(startDate));
        }
        window.location.href = '/cart';
      })
      .catch(function (err) {
        if (window.TRAnalytics) {
          TRAnalytics.addToCartFailed(plan.name, err.message || 'unknown');
        }
        alert(err.message || 'カートへの追加に失敗しました。');
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'カートに入れる';
        }
      });
  }

  // ── Rendering ──
  function render() {
    if (!rootEl) return;

    if (state.loading) {
      rootEl.innerHTML = '<div class="rc-loading">予約カレンダーを読み込み中...</div>';
      return;
    }

    if (state.error) {
      rootEl.innerHTML =
        '<div class="rc-header"><span class="rc-header__spacer"></span>' +
        '<span class="rc-header__title">予約カレンダー</span>' +
        '<button type="button" class="rc-header__close" data-close-dialog aria-label="閉じる">&times;</button></div>' +
        '<div class="rc-error">' + escapeHtml(state.error) + '</div>';
      bindCloseOnly();
      return;
    }

    var productTitle = rootEl.dataset.productTitle || 'レンタル予約';
    var plan = getSelectedPlan();
    var ext = getSelectedExtension();

    var html = '';

    // Header — centered title + close
    html += '<div class="rc-header">';
    html += '<span class="rc-header__spacer"></span>';
    html += '<span class="rc-header__title">' + escapeHtml(productTitle) + '</span>';
    html += '<button type="button" class="rc-header__close" data-close-dialog aria-label="閉じる">&times;</button>';
    html += '</div>';

    // Plans 2x2 + extension pills
    html += '<div class="rc-section rc-section--plans">';
    html += renderPlans();
    if (EXTENSIONS.length > 1) {
      html += renderExtensions();
    }
    html += '</div>';

    // Calendar
    html += '<div class="rc-section">' + renderCalendar() + '</div>';

    // Delivery / Return
    html += renderDeliveryReturn();

    // Time slot trigger
    html += renderSlotTrigger();

    // Bottom bar
    html += renderBottomBar(plan, ext);

    // Time slot overlay (absolute — never resizes the widget)
    if (state.timeslotOpen) {
      html += renderSlotOverlay();
    }

    rootEl.innerHTML = html;
    bindEvents();
  }

  function renderPlans() {
    // 延長行には「延長」ラベルがあるのにプラン行には無く、何を選ぶ行なのかが
    // 示されていなかった（2026-08-08 ノブヒロ指摘）。「プラン」より具体的な
    // 「レンタル期間」にする（カード自体が期間＋価格なので）。
    var html = '<div class="rc-plans__label" id="rc-plans-label">レンタル期間</div>';
    // 選択状態が is-selected（見た目）だけだと支援技術に伝わらないので aria-pressed。
    // 4つが同じ性質の択一トグルなので、選択中/非選択の両方に必ず出す。
    // 早割（NOB-1616）: 対象日を選んでいる間は全カードを取り消し線＋割引後価格にする
    //（プランを切り替えても同じ開始日なら対象のままなので、全カードが対象）。
    var eb = currentEarlyBird();
    html += '<div class="rc-plans" role="group" aria-labelledby="rc-plans-label">';
    for (var i = 0; i < PLANS.length; i++) {
      var plan = PLANS[i];
      var sel = i === state.selectedPlanIndex;
      html += '<button type="button" class="rc-plan' + (sel ? ' is-selected' : '') +
        (eb.eligible ? ' rc-plan--eb' : '') + '"' +
        ' aria-pressed="' + (sel ? 'true' : 'false') + '" data-plan-index="' + i + '">';
      html += '<span class="rc-plan__name">' + escapeHtml(plan.name) + '</span>';
      if (eb.eligible) {
        html += '<span class="rc-plan__price"><s class="rc-plan__was">' + formatPrice(plan.price) + '</s>' +
          formatPrice(plan.price - earlyBirdAmount(plan.price, eb.percent)) + '</span>';
      } else {
        html += '<span class="rc-plan__price">' + formatPrice(plan.price) + '</span>';
      }
      html += '</button>';
    }
    html += '</div>';
    return html;
  }

  function renderExtensions() {
    var ext = getSelectedExtension();
    // rc-ext--eb: 早割の設定がある間は料金が「予約時 +¥2,500 +¥2,250」と長くなるので、
    // CSS が 429px 以下で料金を 2 行目に回す（390px で右端が切れていた・2026-10-23）。
    // 日付で付け外ししないのは、対象日と対象外の日を選び直すたびに行の高さが動かないようにするため
    var html = '<div class="rc-ext' + (EARLY_BIRD ? ' rc-ext--eb' : '') + '">';
    // 「延長」は注文後の延長手続きに読め、予約時に日数を足せることが
    // 伝わらない（NOB-1440: 4泊5日を選べないように見えると問い合わせ発生）。
    html += '<span class="rc-ext__label" id="rc-ext-label">日数を追加</span>';
    html += '<div class="rc-ext__pills" role="group" aria-labelledby="rc-ext-label">';
    for (var j = 0; j < EXTENSIONS.length; j++) {
      var e = EXTENSIONS[j];
      var sel = j === state.selectedExtensionIndex;
      html += '<button type="button" class="rc-pill' + (sel ? ' is-selected' : '') + '"' +
        ' aria-pressed="' + (sel ? 'true' : 'false') + '" data-ext-index="' + j + '">';
      html += escapeHtml(e.days === 0 ? 'なし' : '+' + e.days + '日');
      html += '</button>';
    }
    html += '</div>';
    // 「予約時」を付けるのは、商品ページの説明に出る「ご利用中の延長」料金
    // （S26/S25 4,000円・S24 3,500円）と金額が違うため。ここのピルは予約時に
    // まとめて追加したときの安いほう（同 3,000円 / 2,000円）なので、修飾なしで
    // 金額だけ出すと、同じページに理由の分からない2つの延長料金が並ぶ。
    // spanは選択の有無に関わらず常に出す。320px級の狭幅では価格注記が
    // 2行目に回る（CSS側の media query）ため、選択時だけspanが生えると
    // 行構成が状態で変わり行高がガタつく。空の時も&nbsp;で行を確保する。
    // 早割（NOB-1616）: 対象日を選んでいる間は延長料金も取り消し線＋割引後（下部合計は延長込みで
    // 割引後なので、部品の価格と合計が合うようにする・Codex P2）。
    var eb = currentEarlyBird();
    var extPriceHtml = '&nbsp;';
    if (ext.price > 0) {
      extPriceHtml = eb.eligible
        ? '予約時 <s class="rc-ext__was">+' + formatPrice(ext.price) + '</s> +' + formatPrice(eb.extDiscounted)
        : '予約時 +' + formatPrice(ext.price);
    }
    html += '<span class="rc-ext__price">' + extPriceHtml + '</span>';
    html += '</div>';
    return html;
  }

  function renderCalendar() {
    var year = state.currentMonth.getFullYear();
    var month = state.currentMonth.getMonth();
    var today = todayJST();
    var canGoPrev = !(year === today.getFullYear() && month === today.getMonth());
    // Cap forward navigation to the month containing max_date (fallback: +3 months).
    var maxMonth = state.maxDate
      ? new Date(state.maxDate.getFullYear(), state.maxDate.getMonth(), 1)
      : new Date(today.getFullYear(), today.getMonth() + 3, 1);
    var canGoNext = state.currentMonth < maxMonth;

    var html = '<div class="rc-cal">';

    // 遷移先の月名をボタンに出す（2026-08-08）。矢印だけだと「先の月も
    // 予約できる」ことに気づかない層がいる。押せない方向はラベルを出さない
    // （行けない月を示唆しないため）。
    var prevLabel = ((month + 11) % 12) + 1;
    var nextLabel = ((month + 1) % 12) + 1;
    html += '<div class="rc-cal__nav">';
    html += '<button type="button" class="rc-cal__navbtn rc-cal__navbtn--prev" data-action="prev"' +
      (canGoPrev ? '' : ' disabled') + ' aria-label="前の月へ">&lsaquo;' +
      (canGoPrev ? '<span class="rc-cal__navlabel">' + prevLabel + '月</span>' : '') + '</button>';
    html += '<div class="rc-cal__title">' + year + '年 ' + (month + 1) + '月</div>';
    html += '<button type="button" class="rc-cal__navbtn rc-cal__navbtn--next" data-action="next"' +
      (canGoNext ? '' : ' disabled') + ' aria-label="次の月へ">' +
      (canGoNext ? '<span class="rc-cal__navlabel">' + nextLabel + '月</span>' : '') + '&rsaquo;</button>';
    html += '</div>';

    html += '<div class="rc-cal__dow">';
    for (var w = 0; w < 7; w++) {
      var dowClass = w === 0 ? ' rc-cal__dow--sun' : w === 6 ? ' rc-cal__dow--sat' : '';
      html += '<div class="rc-cal__dowcell' + dowClass + '">' + WEEKDAY_LABELS[w] + '</div>';
    }
    html += '</div>';

    // Always 6 weeks (42 cells) starting from the Sunday of week 1
    var first = new Date(year, month, 1);
    var startCell = addDays(first, -first.getDay());
    var endDate = getEndDate();
    var totalDays = getTotalDays();

    // 早割（NOB-1616）: 境目の日（今日 + minLeadDays）。空きは見ない（規則の境目なので）。
    var ebFirst = firstEarlyBirdDate(today, EARLY_BIRD);

    html += '<div class="rc-cal__grid">';
    for (var c = 0; c < 42; c++) {
      var dateObj = addDays(startCell, c);
      var dateStr = formatDate(dateObj);
      var inMonth = dateObj.getMonth() === month;
      var dow = dateObj.getDay();

      var disabled = isDateDisabled(dateObj);
      var rangeOk = isRangeAvailable(dateObj, totalDays);
      var clickable = !disabled && rangeOk;

      var isStart = state.selectedStartDate && isSameDay(dateObj, state.selectedStartDate);
      var isEnd = endDate && isSameDay(dateObj, endDate) && !isStart;
      var inRange = state.selectedStartDate && endDate &&
        dateObj > state.selectedStartDate && dateObj < endDate;
      var low = clickable && isLowStock(dateStr);
      // 早割の対象日は「選べる日」にだけ色を付ける（埋まった日に付けても嘘になる）。
      var ebDay = clickable && isEarlyBirdLead(leadDaysBetween(dateObj, today), EARLY_BIRD);
      var ebBoundary = !!ebFirst && isSameDay(dateObj, ebFirst);

      var classes = ['rc-day'];
      if (!inMonth) classes.push('rc-day--out');
      if (isStart || isEnd) classes.push('rc-day--selected');
      if (isStart) classes.push('rc-day--start');
      else if (isEnd) classes.push('rc-day--end');
      else if (inRange) classes.push('rc-day--range');
      if (!clickable) classes.push('rc-day--disabled');
      if (state.rejection && state.rejection.dateStr === dateStr) classes.push('rc-day--rejected');
      if (low) classes.push('rc-day--low');
      if (ebDay) classes.push('rc-day--eb');
      if (ebBoundary) classes.push('rc-day--eb-first');
      if (dow === 0) classes.push('rc-day--sun');
      else if (dow === 6) classes.push('rc-day--sat');

      // 読み上げ名が数字だけだと、前後月のセルと区別が付かない（6週42セル固定なので
      // 「1」「2」が月内と月外で必ず重複する）。月日+曜日+空き状況まで名前に入れる。
      // 空き状況の語彙は凡例と同じ（空きあり / 残りわずか / 予約不可）で揃える。
      // low の判定はサーバ由来の isLowStock() をそのまま使う＝クライアント側に
      // しきい値を持たない方針（NOB-1290）を維持。
      // 選択済みの日は空き状況より選択状態を優先する。開始日を選ぶと期間内の日は
      // clickable=false になるので、素直に繋ぐと自分で選んだ日が
      // 「予約不可・レンタル期間中」と読み上げられて不可に聞こえる。
      var dayState;
      if (isStart) dayState = 'レンタル開始日として選択中';
      else if (isEnd) dayState = 'レンタル終了日';
      else if (inRange) dayState = 'レンタル期間中';
      else dayState = !clickable ? '予約不可' : low ? '残りわずか' : '空きあり';
      if (ebDay) dayState += '・' + earlyBirdLabel(EARLY_BIRD);
      var dayLabel = formatMDW(dateObj) + ' ' + dayState;

      html += '<button type="button" class="' + classes.join(' ') + '"' +
        ' data-date="' + dateStr + '"' +
        ' aria-label="' + escapeAttr(dayLabel) + '"' +
        // disabled ではなく aria-disabled。グレー日もタップを受けて却下バナーで
        // 理由を返す仕様（NOB-1123）なので、本当に無効化するとその導線が死ぬ。
        (clickable ? '' : ' data-disabled aria-disabled="true"') +
        '><span class="rc-day__n">' + dateObj.getDate() + '</span>' +
        // 境目のタグはセルの上辺に重ねる absolute の小片（CSS）。セルは1行のまま＝高さを変えない。
        // 読み上げは aria-label（早割対象）で足りるので aria-hidden。
        (ebBoundary ? '<span class="rc-day__ebtag" aria-hidden="true">早割はここから</span>' : '') +
        '</button>';
    }
    html += '</div>';

    // Legend (NOB-1261): the low-stock dot had no explanation.
    html += '<div class="rc-legend">';
    html += '<span class="rc-legend__item"><span class="rc-legend__swatch rc-legend__swatch--ok"></span>空きあり</span>';
    html += '<span class="rc-legend__item"><span class="rc-legend__swatch rc-legend__swatch--low"></span>残りわずか</span>';
    html += '<span class="rc-legend__item"><span class="rc-legend__swatch rc-legend__swatch--na"></span>予約不可</span>';
    if (EARLY_BIRD) {
      html += '<span class="rc-legend__item"><span class="rc-legend__swatch rc-legend__swatch--eb"></span>' +
        escapeHtml(earlyBirdLabel(EARLY_BIRD)) + '</span>';
    }
    html += '</div>';

    html += '</div>';
    return html;
  }

  // 却下バナー（NOB-1123）。有効な選択が無い時のお届け/返却行は「— → —」の
  // プレースホルダしか出していない死にスペースなので、そこを置き換える＝
  // モーダルの高さを変えない（NOB-1047 の1画面完結を維持）。
  // 「この日程は残り◯台」バッジ（旧NOB-1261）は2026-08-08に撤去 —
  // 希少性の表示はカレンダーのオレンジドット＋凡例に一本化（ノブヒロ判断）。
  function renderRejection() {
    var built = buildRejection(parseDate(state.rejection.dateStr), state.rejection.reason);
    var html = '<div class="rc-dr rc-reject" role="status" aria-live="polite">';
    html += '<div class="rc-reject__msg">' + escapeHtml(built.message) + '</div>';
    if (built.chips.length) {
      html += '<div class="rc-reject__chips">';
      for (var i = 0; i < built.chips.length; i++) {
        var c = built.chips[i];
        if (c.kind === 'alt') {
          html += '<a class="rc-chip" href="' + escapeAttr(c.href) + '"' +
            ' data-chip-alt="' + escapeAttr(c.slug) + '">' + escapeHtml(c.label) + '</a>';
        } else {
          html += '<button type="button" class="rc-chip" data-chip-earliest' +
            ' data-chip-date="' + escapeAttr(c.dateStr) + '">' + escapeHtml(c.label) + '</button>';
        }
      }
      html += '</div>';
    }
    html += '</div>';
    return html;
  }

  // 日付未選択のとき、お届け/返却行は「— → —」で何も伝えていない。
  // 開いた直後に「次に何をすればいいか」を示す（2026-08-08 GA4で
  // open後に日付を選ばず閉じる層が最大級の離脱バケットと確認）。
  function renderDateHint() {
    return '<div class="rc-dr rc-hint">' +
      '<div class="rc-hint__msg">開始日をお選びください</div>' +
      '</div>';
  }

  function renderDeliveryReturn() {
    if (state.rejection) return renderRejection();
    if (!state.selectedStartDate) return renderDateHint();
    var has = !!state.selectedStartDate;
    var endDate = getEndDate();
    var slotLabel = getSlotLabel();

    // 見出しはカート・決済画面・注文メールと同じ「レンタル開始日／レンタル終了日」
    // （明細のプロパティ名。2026-10-23 ノブヒロ判断・以前は「お届け／返却」）。
    // 「いつ届くか・どう返すか」は下の小さい字に残す。
    var deliverySub = '時間帯 未選択';
    if (state.selectedTimeSlot === 'none') deliverySub = '指定なしでお届け';
    else if (slotLabel) deliverySub = slotLabel + ' にお届け';

    var html = '<div class="rc-dr">';
    html += dateBox('レンタル開始日', has ? formatMDW(state.selectedStartDate) : '—',
      has ? deliverySub : '', has);
    html += '<div class="rc-dr__arrow">→</div>';
    html += dateBox('レンタル終了日', has && endDate ? formatMDW(endDate) : '—',
      has ? 'ポスト投函で返却' : '', has);
    html += '</div>';
    return html;
  }

  function dateBox(label, value, sub, active) {
    var html = '<div class="rc-datebox' + (active ? ' is-active' : '') + '">';
    html += '<div class="rc-datebox__label">' + escapeHtml(label) + '</div>';
    html += '<div class="rc-datebox__value">' + escapeHtml(value) + '</div>';
    // Always reserve the sub line so the box height never changes
    html += '<div class="rc-datebox__sub">' + (sub ? escapeHtml(sub) : '&nbsp;') + '</div>';
    html += '</div>';
    return html;
  }

  function renderSlotTrigger() {
    var slotLabel = getSlotLabel();
    // 日付選択済み＆時間帯未選択＝残る操作はここだけ。「次はここ」を
    // 薄い黄色で示し、日付選択の直後だけ1回パルスさせる（2026-08-08
    // ノブヒロ指摘: ボタンの存在感が薄く次の一手が分からない）。
    var isNext = !!state.selectedStartDate && !slotLabel;
    var classes = 'rc-slottrigger' +
      (slotLabel ? ' is-selected' : '') +
      (isNext ? ' rc-slottrigger--next' : '') +
      (isNext && state.slotNudge ? ' rc-slottrigger--pulse' : '');
    state.slotNudge = false; // パルスは日付選択直後の1renderだけ
    var html = '<div class="rc-section rc-section--slot">';
    html += '<button type="button" class="' + classes + '" data-open-slot>';
    html += '<span>' + (slotLabel ? '配達時間帯：' + escapeHtml(slotLabel) : '配達時間帯を選択') + '</span>';
    html += '<span class="rc-slottrigger__chev">&rsaquo;</span>';
    html += '</button>';
    html += '</div>';
    return html;
  }

  function renderSlotOverlay() {
    var html = '<div class="rc-overlay">';
    html += '<div class="rc-overlay__backdrop" data-close-slot></div>';
    html += '<div class="rc-sheet">';
    html += '<div class="rc-sheet__head">';
    html += '<span class="rc-sheet__title">配達時間帯</span>';
    html += '<button type="button" class="rc-sheet__close" data-close-slot aria-label="閉じる">&times;</button>';
    html += '</div>';
    html += '<div class="rc-sheet__grid">';
    for (var i = 0; i < TIME_SLOTS.length; i++) {
      var slot = TIME_SLOTS[i];
      var sel = state.selectedTimeSlot === slot.value;
      html += '<button type="button" class="rc-slot' + (sel ? ' is-selected' : '') + '" data-timeslot="' + slot.value + '">';
      html += escapeHtml(slot.label);
      html += '</button>';
    }
    html += '</div>';
    html += '</div>';
    html += '</div>';
    return html;
  }

  function renderBottomBar(plan, ext) {
    // 「3泊4日 +1日延長」: カート・決済画面・注文メール（延長: +1日延長／
    // 「レンタル延長（…）」の明細）と同じ言葉にする（2026-10-23 ノブヒロ判断。
    // 2026-09-09 から「+N日追加」だったが、カートに入れた途端に「延長」へ変わっていた）。
    // 選ぶ所の見出し「日数を追加」は NOB-1440 の対策なのでそのまま。
    // 「4泊5日」への換算表示はしない（ノブヒロ判断 2026-09-09）
    // — カート・注文明細はバリアント名「3泊4日」＋延長明細のままなので、
    // ここだけ換算するとカートと語彙が割れて別プランに見える。
    var detail = plan.name + (ext.days > 0 ? ' +' + ext.days + '日延長' : '');
    // 早割（NOB-1616）: 対象日を選んでいれば合計は割引後。対象外・設定なしなら元の合計。
    var eb = currentEarlyBird();
    var total = eb.total;
    state.earlyBirdShown = eb.eligible;
    var ready = !!state.selectedStartDate && !!state.selectedTimeSlot;
    var ctaLabel = !state.selectedStartDate
      ? '日程を選択'
      : !state.selectedTimeSlot
        ? '配達時間帯を選択'
        : 'カートに入れる';

    var html = '<div class="rc-bottom' + (EARLY_BIRD ? ' rc-bottom--eb' : '') + '">';
    html += '<div class="rc-bottom__info">';
    html += '<div class="rc-bottom__detail">' + escapeHtml(detail) + '</div>';
    // 「この金額以外にかかるか」は決済直前の最大の不安。往復送料込みは
    // 事実（CLAUDE.md: 往復送料込み・追加送料なし・全国一律）なので価格の
    // 真横で答える（2026-08-08）。
    html += '<div class="rc-bottom__price">' + formatPrice(total) +
      '<span class="rc-bottom__tax">税込・往復送料込み</span></div>';
    html += '</div>';
    // 日付選択済み・時間帯未選択は「押すと時間帯シートが開く」中間状態。
    // 押せる見た目にする（is-actionable）。日付すら未選択の時だけ無効表示。
    var actionable = !ready && !!state.selectedStartDate;
    html += '<button type="button" class="rc-cta' +
      (ready ? ' is-ready' : (actionable ? ' is-actionable' : '')) +
      '" data-cta>' + escapeHtml(ctaLabel) + '</button>';
    if (EARLY_BIRD) html += renderEarlyBirdRow(eb);
    html += '</div>';
    return html;
  }

  // 下部バーの早割行（NOB-1616）。設定がある間は常に1行ぶん確保し、状態で中身だけ変える
  // （行が生えたり消えたりすると日付を選んだ瞬間にバーの高さが跳ねる）。
  //   対象日を選んでいる … 「早割 10%オフ −¥1,280」＋注記「お支払いが開始日のN日以上前なら適用」
  //   それ以外           … 「M/D以降の開始なら10%オフ」（最寄りの選べる対象日・タップで移動）
  //   対象日が1つも選べない … 境目の日付だけを静的に示す
  // 「今予約すると」型のアンカー表現・「満室」は使わない（既存ルール）。
  function renderEarlyBirdRow(eb) {
    var html = '<div class="rc-bottom__eb">';
    if (eb.eligible) {
      html += '<span class="rc-bottom__eb-msg">' + escapeHtml(earlyBirdLabel(EARLY_BIRD)) +
        ' −' + formatPrice(eb.discount) + '</span>';
      html += '<span class="rc-bottom__eb-note">' + escapeHtml(earlyBirdNoteText(EARLY_BIRD)) + '</span>';
    } else {
      var target = earlyBirdNudgeDate();
      if (target) {
        html += '<button type="button" class="rc-bottom__eb-nudge" data-eb-nudge' +
          ' data-eb-date="' + formatDate(target) + '">' +
          escapeHtml(earlyBirdNudgeText(target, EARLY_BIRD)) +
          '<span class="rc-bottom__eb-chev" aria-hidden="true">&rsaquo;</span></button>';
      } else {
        var first = firstEarlyBirdDate(todayJST(), EARLY_BIRD);
        html += '<span class="rc-bottom__eb-note">' + escapeHtml(earlyBirdLabel(EARLY_BIRD) +
          'は' + formatMD(first) + '以降の開始が対象') + '</span>';
      }
    }
    html += '</div>';
    return html;
  }

  // ── Event binding ──
  function bindEvents() {
    if (!rootEl) return;

    each('.rc-plan', function (el) {
      el.addEventListener('click', (function (idx) {
        return function () { onPlanSelect(idx); };
      })(parseInt(el.dataset.planIndex, 10)));
    });

    each('.rc-pill', function (el) {
      el.addEventListener('click', (function (idx) {
        return function () { onExtensionSelect(idx); };
      })(parseInt(el.dataset.extIndex, 10)));
    });

    each('.rc-slot[data-timeslot]', function (el) {
      el.addEventListener('click', (function (val) {
        return function () { onTimeSlotSelect(val); };
      })(el.dataset.timeslot));
    });

    each('.rc-day[data-date]', function (el) {
      if (el.hasAttribute('data-disabled')) {
        el.addEventListener('click', (function (dateStr, elRef) {
          return function () {
            var reason = disabledReason(parseDate(dateStr));
            if (window.TRAnalytics) {
              TRAnalytics.dateUnavailable(dateStr, reason);
            }
            if (reason === 'past' || reason === 'disabled') {
              // バナーは出さない（過去日に説明はノイズ）が、完全な無反応は
              // 壊れて見えるので首振りモーションだけ返す（2026-08-08）。
              elRef.classList.remove('rc-day--nope');
              void elRef.offsetWidth; // 連打時にアニメーションを再始動させる
              elRef.classList.add('rc-day--nope');
              return;
            }
            onRejectedTap(dateStr, reason);
          };
        })(el.dataset.date, el));
      } else {
        el.addEventListener('click', (function (dateStr) {
          return function () { onDayClick(dateStr); };
        })(el.dataset.date));
      }
    });

    // 却下バナーのチップ（NOB-1123）
    each('[data-chip-alt]', function (el) {
      el.addEventListener('click', function () {
        // 遷移はaタグのhrefに任せ、ここでは計測だけ行う。
        // 値は既存の related-products 経路（商品名）と語彙を揃える —
        // スラッグを混ぜると同じ機種がGA4上で別グループに割れる（レビュー指摘）。
        // 経路の区別は context パラメータで行う。
        if (window.TRAnalytics) {
          var slug = el.getAttribute('data-chip-alt');
          var altName = slug;
          for (var ai = 0; ai < ALT_PRODUCTS.length; ai++) {
            if (ALT_PRODUCTS[ai].slug === slug) {
              // 正式な商品タイトルを優先（related-products経路と同じ語彙）。
              altName = ALT_PRODUCTS[ai].title || ALT_PRODUCTS[ai].label;
              break;
            }
          }
          TRAnalytics.alternativeClicked(
            rootEl.dataset.productTitle || rootEl.dataset.product || '',
            altName,
            'rejection_chip'
          );
        }
      });
    });
    each('[data-chip-earliest]', function (el) {
      el.addEventListener('click', function () {
        var dateStr = el.getAttribute('data-chip-date');
        if (window.TRAnalytics && TRAnalytics.rejectionChipClicked) {
          // attempted は顧客が試した日、チップの推奨日は target で別送
          //（推奨日を attempted に入れると date_unavailable とJOINできない）。
          TRAnalytics.rejectionChipClicked(
            'earliest_date',
            state.rejection ? state.rejection.dateStr : dateStr,
            state.rejection && state.rejection.reason,
            dateStr
          );
        }
        onDayClick(dateStr);
      });
    });

    // 早割のひと押し（NOB-1616）
    each('[data-eb-nudge]', function (el) {
      el.addEventListener('click', function () {
        onEarlyBirdNudge(el.getAttribute('data-eb-date'));
      });
    });

    var prevBtn = rootEl.querySelector('[data-action="prev"]');
    var nextBtn = rootEl.querySelector('[data-action="next"]');
    if (prevBtn && !prevBtn.disabled) prevBtn.addEventListener('click', onPrevMonth);
    if (nextBtn && !nextBtn.disabled) nextBtn.addEventListener('click', onNextMonth);

    var slotTrigger = rootEl.querySelector('[data-open-slot]');
    if (slotTrigger) slotTrigger.addEventListener('click', onOpenSlot);

    each('[data-close-slot]', function (el) {
      el.addEventListener('click', onCloseSlot);
    });

    var cta = rootEl.querySelector('[data-cta]');
    if (cta) cta.addEventListener('click', onCta);

    bindCloseOnly();
  }

  function bindCloseOnly() {
    if (!rootEl) return;
    var closeBtn = rootEl.querySelector('[data-close-dialog]');
    if (closeBtn) closeBtn.addEventListener('click', closeDialog);
  }

  function each(selector, fn) {
    var els = rootEl.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) fn(els[i]);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // 属性値用。escapeHtml() は textNode→innerHTML なので & < > しか潰さず、
  // 引用符が素通りする＝属性の中に入れると囲みを抜けられる。
  // 今の呼び出し元の値は全部内部生成で引用符を含まないが、
  // 属性に入れるものはこちらを通す（将来 API 由来の文字列が来ても壊れない）。
  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Modal open / close ──
  function setupModal() {
    var dialog = document.getElementById('rental-calendar-dialog');
    // Any element carrying [data-calendar-trigger] also opens the dialog
    // (e.g. a future sticky CTA), reporting its data-trigger-location so
    // calendar_opened can attribute opens per trigger.
    var openBtns = document.querySelectorAll(
      '#rental-calendar-open, [data-calendar-trigger]'
    );
    if (!dialog || !openBtns.length) return;

    function bindTrigger(btn) {
      btn.addEventListener('click', function (ev) {
        // An <a href="#rental-calendar-open"> trigger is a no-JS fallback to
        // the button; with JS we open the dialog instead of jumping to the
        // anchor. (None on the page today — the availability summary's
        // duplicate link was removed on 2026-10-17.)
        if (btn.tagName === 'A') ev.preventDefault();
        dialog.showModal();
        if (window.TRAnalytics) {
          TRAnalytics.calendarOpened(
            !!state.availability,
            (btn.dataset && btn.dataset.triggerLocation) || 'inline'
          );
        }
        reportEarlyBird('shown', null);
      });
    }
    for (var i = 0; i < openBtns.length; i++) bindTrigger(openBtns[i]);

    // Fires once on any close path (button, backdrop, ESC, post-add-to-cart),
    // capturing how far the user got — the key drop-off signal.
    dialog.addEventListener('close', function () {
      if (window.TRAnalytics) {
        TRAnalytics.calendarClosed(
          currentFunnelStep(),
          state.addedToCart,
          state.selectedStartDate ? formatDate(state.selectedStartDate) : '',
          getTotalDays()
        );
      }
      // 次回オープン時に前回の却下バナーを持ち越さない
      if (state.rejection) {
        state.rejection = null;
        render();
      }
    });

    // Close on backdrop click
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        dialog.close();
      }
    });
  }

  // ── Node（tests/early-bird.test.js）向けの公開 ──
  // document の無い環境では DOM 初期化をせず、早割の純関数だけを module.exports で公開する
  // （assets/cart-deadline-guard.js と同じ作法）。ブラウザではこのブロックは何もしない。
  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = {
        parseEarlyBirdConfig: parseEarlyBirdConfig,
        leadDaysBetween: leadDaysBetween,
        isEarlyBirdLead: isEarlyBirdLead,
        firstEarlyBirdDate: firstEarlyBirdDate,
        nearestEarlyBirdDate: nearestEarlyBirdDate,
        earlyBirdAmount: earlyBirdAmount,
        earlyBirdPricing: earlyBirdPricing,
        earlyBirdLabel: earlyBirdLabel,
        earlyBirdNudgeText: earlyBirdNudgeText,
        earlyBirdNoteText: earlyBirdNoteText,
        earlyBirdDisplayStale: earlyBirdDisplayStale,
        formatPercent: formatPercent,
        addDays: addDays,
        formatDate: formatDate,
        formatMD: formatMD,
        parseDate: parseDate
      };
    }
    return;
  }

  // ── Boot ──
  function boot() {
    init();
    setupModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
